import React, { useEffect } from "react";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";

import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";

interface ConfirmDeleteModalProps {
  isDeleteModalOpen: boolean;
  isLoadingIQDelete?: boolean;
  modalContent: string;
  modalTitle: string;
  onRemoveHandler?: () => void;
  setIsDeleteModalOpen: (arg: boolean) => void;
  buttonTexts?: {
    cancelText: string;
    successText: string;
  };
  isSuccessTextDisabled?: boolean;
}

export function ConfirmDeleteModal(props: ConfirmDeleteModalProps) {
  const {
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    onRemoveHandler,
    modalTitle,
    modalContent,
    isLoadingIQDelete,
    buttonTexts,
    isSuccessTextDisabled = false,
  } = props;

  const { enableScope, disableScope } = useHotkeysContext();

  // While the confirm dialog is open, disable the review-scoped hotkeys (e.g.
  // count/bounding-box and text-recognition "Enter" submit) so a single Enter
  // can only confirm the deletion and never also submit a label. The modal's
  // own Enter handler below is unscoped, so it keeps working.
  useEffect(() => {
    if (isDeleteModalOpen) {
      disableScope("review");
    } else {
      enableScope("review");
    }

    return () => {
      enableScope("review");
    };
  }, [isDeleteModalOpen, disableScope, enableScope]);

  const handleCloseModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleClickSuccess = () => {
    if (onRemoveHandler) {
      onRemoveHandler();
    }
  };

  const canConfirm = isDeleteModalOpen && !!onRemoveHandler && !isSuccessTextDisabled && !isLoadingIQDelete;

  useHotkeys(
    "enter",
    (event) => {
      event.preventDefault();
      handleClickSuccess();
    },
    { enabled: canConfirm },
    [canConfirm, onRemoveHandler]
  );

  useHotkeys(
    "esc",
    () => {
      handleCloseModal();
    },
    { enabled: isDeleteModalOpen },
    [isDeleteModalOpen, setIsDeleteModalOpen]
  );

  return (
    <Modal
      open={isDeleteModalOpen}
      onClose={handleCloseModal}
      size="small"
      title={modalTitle}
      onCancel={handleCloseModal}
      cancelButtonTitle={buttonTexts?.cancelText || "Cancel"}
      onConfirmation={handleClickSuccess}
      confirmationButtonTitle={buttonTexts?.successText || "Ok"}
      isConfirmationLoading={isLoadingIQDelete}
      isConfirmationDisabled={isSuccessTextDisabled}
      body={[
        /* Dont allow user to set his own modal content here */
        <Typography
          id="modal-modal-description"
          sx={{ mt: 2 }}
          align="center"
          className="text-black"
          dangerouslySetInnerHTML={{ __html: modalContent }}
        />,
      ]}
    />
  );
}
